/**
 * The ring: a colleague is calling, answer or decline.
 *
 * It is an OVERLAY ENTRY, on the app's existing stack, because that is exactly
 * what the registry is for — a transient notification with plain-data props.
 * The CALL itself could not live there and does not; a room holds a live
 * session and outlives the route that opened it, which is why
 * `ConferencingHost` sits beside the stack instead.
 *
 * ITS PROPS ARE ONE STRING. Everything else — who is calling, since when —
 * comes from `ringStore`, because the ring is that store's fact and a copy in
 * a stack entry would be a second one. The entry only needs to know WHICH ring
 * it is drawing, so that a stale entry left behind by a replaced ring draws
 * nothing rather than the wrong caller.
 *
 * NOT MODAL, and not merely for taste: a modal ring would take the keyboard
 * away from a clinician mid-note to tell them the phone is going. It sits in
 * the corner, above the call surface, and the chart underneath stays usable.
 *
 * NO EXIT ANIMATION, so `onExited` fires as soon as `open` goes false. An
 * animated entry whose transition never fires — display:none, reduced motion,
 * a background tab — is an overlay row that never leaves the stack.
 */

import { getTRPCClient } from "@repo/api";
import { usePractitioners } from "@repo/data-store";
import { Button } from "@repo/ui/components/ui/button";
import { PhoneIcon, PhoneOffIcon } from "lucide-react";
import { useEffect } from "react";
import type { OverlayControlProps } from "@/app/overlays/registry";
import { practitionerDisplayName } from "@/shared/lib/practitioner-utils";
import { openCallRoom } from "../call-room";
import { prepareCallAudio } from "../call-sound-synthesis";
import { endRing, useIncomingRing } from "../ring-store";

export interface IncomingCallRingProps {
  /** Which ring this entry is drawing. */
  callId: string;
}

export function IncomingCallRing({ callId, open, onExited }: IncomingCallRingProps & OverlayControlProps) {
  useEffect(() => {
    if (!open) onExited();
  }, [open, onExited]);

  const ring = useIncomingRing();
  const { practitioners } = usePractitioners();

  // A ring already replaced or settled. The entry is on its way off the stack;
  // drawing the previous caller's name in the meantime would be worse than
  // drawing nothing.
  if (!open || ring?.call.id !== callId) return null;

  const caller = practitioners.find((p) => p.fhirLocator === ring.call.from);
  const callerName = practitionerDisplayName(caller) ?? "A colleague";

  /**
   * ANSWERING IS TWO THINGS, and only one of them is the room. `answer` settles
   * the ring, which is what takes the caller off "ringing" and is the ONLY way
   * they learn somebody picked up; the room's own surface separately asks for a
   * credential with `join`, which moves the ring not at all.
   *
   * Entering does not wait on the server, for `JoinVisitButton`'s reason: it is
   * a store send, there is nothing to debounce, and a refusal — a ring that
   * rang out in the last second — surfaces through the global tRPC error toast
   * and closes the surface from the host. Waiting would buy a spinner and a
   * second place for the same refusal to land.
   */
  const answer = () => {
    // This is the recipient's gesture; they will need audio when they leave.
    prepareCallAudio();
    openCallRoom(callId);
    endRing(callId, "answered");
    void getTRPCClient().colleagueCall.answer.mutate({ callId });
  };

  /**
   * Declining DOES go to the server, and is the only way the caller learns
   * anything: there is no sweeper, so an un-declined ring just goes quiet on
   * their end for a minute. The ring comes down immediately either way — a
   * refusal here means the call was already settled, which is the same outcome.
   */
  const decline = () => {
    endRing(callId, "declined");
    void getTRPCClient().colleagueCall.decline.mutate({ callId });
  };

  return (
    <div
      role="alertdialog"
      aria-label={`${callerName} is calling`}
      className="fixed bottom-6 right-6 z-[60] flex max-w-md items-center gap-4 rounded-2xl border-2 bg-popover py-3 pl-5 pr-3 text-popover-foreground shadow-2xl"
    >
      {/*
       * The name carries the decision — it is the only thing that changes the
       * answer — and TRUNCATES rather than wrapping, which keeps the two buttons
       * in a fixed place whoever is ringing. A control that moves with the
       * length of a name is one the hand has to find again.
       */}
      <div className="min-w-0 flex-1">
        <p className="truncate text-base font-semibold">{callerName}</p>
        {/*
         * THE SHIMMER SAYS IT IS STILL HAPPENING. A ring is live until somebody
         * acts or it rings out, and a still line of text says nothing about
         * which. Motion that goes nowhere is the honest signal for a wait whose
         * length nobody can measure. It stops under reduced motion, where the
         * word still carries it.
         */}
        <p className="animate-text-shimmer bg-[linear-gradient(90deg,var(--muted-foreground)_25%,var(--foreground)_50%,var(--muted-foreground)_75%)] bg-[length:200%_100%] bg-clip-text text-sm text-transparent motion-reduce:animate-none">
          Calling…
        </p>
      </div>

      {/*
       * DECLINE SITS FIRST, away from the thumb's resting place on answer, and
       * the two are colour-separated as well as position-separated: these are
       * the two irreversible ends of the same gesture and a misfire is a call
       * cut off in somebody's face.
       */}
      <Button
        size="icon"
        onClick={decline}
        aria-label={`Decline the call from ${callerName}`}
        title="Decline"
        className="size-12 shrink-0 rounded-full border-transparent bg-red-500 text-white hover:bg-red-400"
      >
        <PhoneOffIcon className="size-5" />
      </Button>
      <Button
        size="icon"
        onClick={answer}
        aria-label={`Answer the call from ${callerName}`}
        title="Answer"
        className="size-12 shrink-0 rounded-full border-transparent bg-emerald-600 text-white hover:bg-emerald-500"
      >
        <PhoneIcon className="size-5" />
      </Button>
    </div>
  );
}
